/**
 * Migration 1 — Unit Hub. Schemas for the per-unit custom-field
 * definition CRUD (`/unit-fields`), per-unit value upserts, and unit
 * documents.
 */

import { z } from 'zod';

import {
  UNIT_CUSTOM_FIELD_TYPES,
  UNIT_DOCUMENT_KINDS,
  type UnitCustomFieldType,
} from './constants.js';

// ---------- Custom-field definitions ----------

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullish()
    .transform((v) => v ?? null);

/** Slug used as the stable identifier on a definition. Mirrors how
 *  device-types' `slug` is validated. */
export const unitCustomFieldKey = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9][a-z0-9_]*[a-z0-9]$|^[a-z0-9]$/, 'Use lowercase letters, digits, and underscores');

export const unitCustomFieldOption = z.object({
  value: z.string().trim().min(1).max(80),
  label: z.string().trim().min(1).max(200),
});
export type UnitCustomFieldOption = z.infer<typeof unitCustomFieldOption>;

export const createUnitCustomFieldDefinitionInput = z
  .object({
    key: unitCustomFieldKey,
    label: z.string().trim().min(1).max(200),
    fieldType: z.enum(UNIT_CUSTOM_FIELD_TYPES),
    options: z.array(unitCustomFieldOption).min(2).max(50).nullish().transform((v) => v ?? null),
    helpText: optionalText(2_000),
    displayOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .superRefine((v, ctx) => {
    const needsOptions = v.fieldType === 'single_select' || v.fieldType === 'multi_select';
    if (needsOptions && (!v.options || v.options.length < 2)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message: 'Provide at least two options for select fields',
      });
    }
    if (!needsOptions && v.options && v.options.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message: 'Options are only valid for single_select / multi_select fields',
      });
    }
    if (needsOptions && v.options) {
      const seen = new Set<string>();
      for (let i = 0; i < v.options.length; i += 1) {
        const opt = v.options[i]!;
        if (seen.has(opt.value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options', i, 'value'],
            message: 'Duplicate option value',
          });
        }
        seen.add(opt.value);
      }
    }
  });
export type CreateUnitCustomFieldDefinitionInput = z.infer<
  typeof createUnitCustomFieldDefinitionInput
>;

/** Editable bits of a definition. `field_type` is intentionally
 *  excluded — once a field is created, the type is frozen because
 *  changing it would invalidate every existing value. Admins archive
 *  + recreate to switch types. */
export const updateUnitCustomFieldDefinitionInput = z
  .object({
    label: z.string().trim().min(1).max(200).optional(),
    options: z.array(unitCustomFieldOption).min(2).max(50).nullish().optional(),
    helpText: optionalText(2_000).optional(),
    displayOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Provide at least one field to update',
  });
export type UpdateUnitCustomFieldDefinitionInput = z.infer<
  typeof updateUnitCustomFieldDefinitionInput
>;

export const reorderUnitCustomFieldsInput = z.object({
  ids: z.array(z.string().uuid()).min(1).max(500),
});
export type ReorderUnitCustomFieldsInput = z.infer<typeof reorderUnitCustomFieldsInput>;

// ---------- Custom-field values ----------

/** A discriminated value-shape used by `unit_custom_field_values.value`.
 *  The runtime parser picks the right branch by `fieldType`. */
export const unitCustomFieldValueShape = z.union([
  z.object({ v: z.string().max(500) }),
  z.object({ v: z.number().finite() }),
  z.object({ v: z.boolean() }),
  z.object({ values: z.array(z.string().max(80)).max(50) }),
]);
export type UnitCustomFieldValueShape = z.infer<typeof unitCustomFieldValueShape>;

export interface ValidatedUnitFieldValue {
  /** The normalized value blob ready to write to JSONB. */
  storage: UnitCustomFieldValueShape;
}

/**
 * Validate a raw user-supplied value against a definition. Returns
 * either `{ ok: true, value }` (storage shape) or `{ ok: false,
 * message }`. Used by both the API (on the value-upsert endpoint) and
 * the UI (so inline-edit can show errors before round-tripping).
 */
export function validateUnitCustomFieldValue(
  fieldType: UnitCustomFieldType,
  options: UnitCustomFieldOption[] | null,
  raw: unknown,
):
  | { ok: true; value: UnitCustomFieldValueShape }
  | { ok: false; message: string } {
  switch (fieldType) {
    case 'text': {
      if (typeof raw !== 'string') return { ok: false, message: 'Expected a string value' };
      if (raw.length > 500) return { ok: false, message: 'Text values are capped at 500 characters' };
      return { ok: true, value: { v: raw } };
    }
    case 'long_text': {
      if (typeof raw !== 'string') return { ok: false, message: 'Expected a string value' };
      if (raw.length > 8_000)
        return { ok: false, message: 'Long-text values are capped at 8 000 characters' };
      return { ok: true, value: { v: raw } };
    }
    case 'date': {
      if (typeof raw !== 'string') return { ok: false, message: 'Expected an ISO date string' };
      if (!/^\d{4}-\d{2}-\d{2}$/.test(raw))
        return { ok: false, message: 'Use YYYY-MM-DD' };
      return { ok: true, value: { v: raw } };
    }
    case 'number': {
      if (typeof raw !== 'number' || !Number.isFinite(raw))
        return { ok: false, message: 'Expected a finite number' };
      return { ok: true, value: { v: raw } };
    }
    case 'yes_no': {
      if (typeof raw !== 'boolean') return { ok: false, message: 'Expected a boolean' };
      return { ok: true, value: { v: raw } };
    }
    case 'single_select': {
      if (typeof raw !== 'string') return { ok: false, message: 'Expected a string option value' };
      const valid = (options ?? []).some((o) => o.value === raw);
      if (!valid) return { ok: false, message: 'Value not in the allowed option list' };
      return { ok: true, value: { v: raw } };
    }
    case 'multi_select': {
      if (!Array.isArray(raw)) return { ok: false, message: 'Expected an array of option values' };
      if (raw.some((x) => typeof x !== 'string'))
        return { ok: false, message: 'All values must be strings' };
      const allowed = new Set((options ?? []).map((o) => o.value));
      for (const x of raw as string[]) {
        if (!allowed.has(x)) return { ok: false, message: `"${x}" is not in the allowed list` };
      }
      // Dedupe + freeze for storage — the UI may send duplicates from
      // bulk-toggle interactions; the canonical form is unique values
      // in input order.
      const seen = new Set<string>();
      const deduped: string[] = [];
      for (const x of raw as string[]) {
        if (!seen.has(x)) {
          seen.add(x);
          deduped.push(x);
        }
      }
      return { ok: true, value: { values: deduped } };
    }
    default: {
      // exhaustive — TypeScript will flag if a new field_type is added
      const _exhaustive: never = fieldType;
      return { ok: false, message: `Unsupported field type: ${String(_exhaustive)}` };
    }
  }
}

export const setUnitCustomFieldValueInput = z.object({
  /** The raw value, shape determined by the field's `field_type`.
   *  Server validates against the definition before persisting. */
  value: z.unknown(),
});
export type SetUnitCustomFieldValueInput = z.infer<typeof setUnitCustomFieldValueInput>;

// ---------- Unit documents ----------

export const unitDocumentKindSchema = z.enum(UNIT_DOCUMENT_KINDS);

export const unitDocumentPresignInput = z.object({
  kind: unitDocumentKindSchema.default('other'),
  filename: z.string().trim().min(1).max(255),
  contentType: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[\w.+-]+\/[\w.+-]+$/, 'Invalid content type'),
  sizeBytes: z.number().int().positive().max(50 * 1024 * 1024),
  caption: optionalText(500),
});
export type UnitDocumentPresignInput = z.infer<typeof unitDocumentPresignInput>;

export const unitDocumentConfirmInput = z.object({
  caption: optionalText(500).optional(),
});
export type UnitDocumentConfirmInput = z.infer<typeof unitDocumentConfirmInput>;

export const updateUnitDocumentInput = z
  .object({
    kind: unitDocumentKindSchema.optional(),
    caption: optionalText(500).optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Provide at least one field to update',
  });
export type UpdateUnitDocumentInput = z.infer<typeof updateUnitDocumentInput>;
